import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultEmail?: string;
  contextHeadline?: string;
  contextSubtext?: string;
}

export function AuthModal({ open, onClose, onSuccess, defaultEmail, contextHeadline, contextSubtext }: AuthModalProps) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);

  useEffect(() => {
    if (open && defaultEmail) {
      setTimeout(() => passwordRef.current?.focus(), 400);
    }
  }, [open, defaultEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setIsLoading(true);

    try {
      // Try sign in first
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

      if (!signInError) {
        toast.success("Welcome back!");
        onSuccess?.();
        onClose();
        return;
      }

      // If invalid credentials, try creating an account
      if (signInError.message === "Invalid login credentials") {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { email } },
        });

        if (signUpError) {
          toast.error(signUpError.message);
          return;
        }

        // Auto-confirm is enabled, so try signing in immediately
        const { error: retryError } = await supabase.auth.signInWithPassword({ email, password });
        if (retryError) {
          toast.success("Account created! Please check your email to verify, then sign in.");
          return;
        }

        toast.success("Account created — welcome!");
        onSuccess?.();
        onClose();
        return;
      }

      toast.error(signInError.message);
    } catch {
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: "google" | "apple") => {
    setOauthLoading(provider);
    let redirected = false;

    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
        extraParams: provider === "google" && email.trim() ? { login_hint: email.trim() } : undefined,
      });

      if (result.error) {
        const message = result.error instanceof Error ? result.error.message : "OAuth sign-in failed";
        toast.error(message);
        return;
      }

      if (result.redirected) {
        redirected = true;
        return;
      }

      toast.success("Signed in successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "OAuth sign-in failed";
      toast.error(message);
    } finally {
      if (!redirected) {
        setOauthLoading(null);
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="relative z-10 mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-border/20 bg-background/60 p-10 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] backdrop-blur-xl"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* Subtle gradient accent */}
            <div className="pointer-events-none absolute -top-24 -right-24 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-accent/10 blur-3xl" />

            <button
              onClick={onClose}
              className="absolute right-5 top-5 rounded-full p-1.5 text-muted-foreground/60 transition-all hover:bg-foreground/5 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative mb-8">
              <h2 className="font-serif text-2xl tracking-tight text-foreground">
                {contextHeadline || "Sign in to save your project"}
              </h2>
              <p className="mt-2 font-sans text-sm font-light leading-relaxed text-muted-foreground">
                {contextSubtext || "Enter your email and password. If you're new, we'll create an account automatically."}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="relative space-y-5">
              <div>
                <Label htmlFor="auth-email" className="font-sans text-[11px] uppercase tracking-[0.1em] text-muted-foreground/70">Email</Label>
                <Input
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 border-border/20 bg-foreground/[0.03] backdrop-blur-sm transition-colors focus:border-foreground/30 focus:bg-foreground/[0.05]"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <Label htmlFor="auth-password" className="font-sans text-[11px] uppercase tracking-[0.1em] text-muted-foreground/70">Password</Label>
                <Input
                  ref={passwordRef}
                  id="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="mt-2 border-border/20 bg-foreground/[0.03] backdrop-blur-sm transition-colors focus:border-foreground/30 focus:bg-foreground/[0.05]"
                  placeholder="••••••••"
                />
              </div>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  onClick={onClose}
                  className="font-sans text-xs font-light text-muted-foreground/60 transition-colors hover:text-foreground"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !!oauthLoading}
                variant="outline"
                className="w-full rounded-none border-foreground/60 py-5 font-sans text-[11px] uppercase tracking-[0.15em] text-foreground transition-all hover:border-foreground hover:bg-foreground hover:text-background"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Continue"
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6 flex items-center">
              <div className="flex-1 border-t border-border/20" />
              <span className="px-4 font-sans text-[11px] uppercase tracking-[0.1em] text-muted-foreground/50">or</span>
              <div className="flex-1 border-t border-border/20" />
            </div>

            {/* OAuth Buttons */}
            <div className="relative space-y-3">
              <Button
                type="button"
                variant="outline"
                disabled={!!oauthLoading}
                onClick={() => handleOAuthSignIn("google")}
                className="w-full gap-3 rounded-none border-border/30 bg-foreground/[0.03] py-5 font-sans text-sm font-light text-foreground backdrop-blur-sm transition-all hover:border-foreground/40 hover:bg-foreground/[0.06]"
              >
                {oauthLoading === "google" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                )}
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                disabled={!!oauthLoading}
                onClick={() => handleOAuthSignIn("apple")}
                className="w-full gap-3 rounded-none border-border/30 bg-foreground/[0.03] py-5 font-sans text-sm font-light text-foreground backdrop-blur-sm transition-all hover:border-foreground/40 hover:bg-foreground/[0.06]"
              >
                {oauthLoading === "apple" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                )}
                Continue with Apple
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
